import React, { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, Copy } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { CostPriceCopySelection } from '../../../../../utils/costPriceClipboard';
import { borderPrimary, primaryText, secondaryText, whiteBG } from '../../../../../styling/stylingHelper';

export default function CopyIngredientsButton({ hasIngredients, hasSubrecipes, onCopy }: {
    hasIngredients: boolean;
    hasSubrecipes: boolean;
    onCopy: (selection: CostPriceCopySelection) => void;
}) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [copied, setCopied] = useState(false);
    const root = useRef<HTMLDivElement>(null);
    const trigger = useRef<HTMLButtonElement>(null);
    const hasOptions = hasIngredients && hasSubrecipes;

    useEffect(() => {
        if (!open) return;
        const closeOutside = (event: MouseEvent) => {
            if (!root.current?.contains(event.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', closeOutside);
        return () => document.removeEventListener('mousedown', closeOutside);
    }, [open]);

    useEffect(() => {
        if (!copied) return;
        const timer = setTimeout(() => setCopied(false), 2000);
        return () => clearTimeout(timer);
    }, [copied]);

    const copy = (selection: CostPriceCopySelection) => {
        onCopy(selection);
        setOpen(false);
        setCopied(true);
        trigger.current?.focus();
    };

    return (
        <div ref={root} className="relative" onBlur={event => {
            if (!event.currentTarget.contains(event.relatedTarget as Node)) setOpen(false);
        }} onKeyDown={event => {
            if (event.key === 'Escape' && open) { event.stopPropagation(); setOpen(false); trigger.current?.focus(); }
        }}>
            <button ref={trigger} type="button" aria-expanded={hasOptions ? open : undefined}
                aria-label={t('costPrice.clipboard.copy')} title={t('costPrice.clipboard.copy')}
                className={`inline-flex min-h-[44px] items-center gap-1.5 px-2 text-sm font-medium rounded-lg ${secondaryText} hover:bg-gray-100 dark:hover:bg-white/5`}
                onClick={() => hasOptions ? setOpen(!open) : copy(hasIngredients ? 'ingredients' : 'subrecipes')}>
                {copied ? <Check size={16} /> : <Copy size={16} />}
                <span className="hidden sm:inline">{t(copied ? 'costPrice.clipboard.copyDone' : 'costPrice.clipboard.copy')}</span>
                {hasOptions && <ChevronDown size={14} />}
            </button>
            {open && (
                <div className={`absolute right-0 top-full z-30 w-52 max-w-[75vw] rounded-lg border shadow-lg py-1 ${borderPrimary} ${whiteBG}`}>
                    {(['ingredients', 'subrecipes', 'both'] as const).map(selection => (
                        <button key={selection} type="button" onClick={() => copy(selection)}
                            className={`block w-full min-h-[44px] px-3 py-2 text-left text-sm ${primaryText} hover:bg-gray-100 dark:hover:bg-white/5`}>
                            {t(`costPrice.clipboard.copy_${selection}`)}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
